import React from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, GitMerge, X } from 'lucide-react';

/**
 * Floating action bar shown at the bottom of a list when the operator has
 * one-or-more rows selected. Used on both the Tours page and the Customer
 * Profile tours tab. Merge is optional (only surfaced where cross-customer
 * merging is safe — i.e. inside a customer's own profile).
 *
 * Props:
 *   selectedCount       — number of currently selected row IDs
 *   onClear             — clears the selection
 *   onDelete            — opens delete confirmation
 *   onMerge             — opens merge confirmation (optional)
 *   merging             — disables buttons while merge in flight
 *   deleting            — disables buttons while delete in flight
 *   minMergeCount       — minimum selection to enable merge (default 2)
 */
const BulkActionBar = ({
  selectedCount,
  onClear,
  onDelete,
  onMerge,
  merging = false,
  deleting = false,
  minMergeCount = 2,
}) => {
  if (selectedCount === 0) return null;
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-20 lg:bottom-6 z-40 flex items-center gap-2 rounded-full bg-foreground text-background shadow-xl px-4 py-2"
      data-testid="bulk-action-bar"
    >
      <button
        type="button"
        onClick={onClear}
        className="rounded-full hover:bg-white/10 p-1 -ml-2"
        data-testid="bulk-clear"
        aria-label="Clear selection"
      >
        <X className="w-4 h-4" />
      </button>
      <span className="text-xs font-semibold" data-testid="bulk-count">
        {selectedCount} selected
      </span>
      {onMerge && (
        <Button
          size="sm"
          variant="secondary"
          className="h-7 text-xs rounded-full"
          onClick={onMerge}
          disabled={merging || deleting || selectedCount < minMergeCount}
          title={selectedCount < minMergeCount ? `Select at least ${minMergeCount} tours to merge` : 'Merge selected tours'}
          data-testid="bulk-merge-btn"
        >
          <GitMerge className="w-3 h-3 mr-1" />
          Merge
        </Button>
      )}
      <Button
        size="sm"
        variant="destructive"
        className="h-7 text-xs rounded-full"
        onClick={onDelete}
        disabled={merging || deleting}
        data-testid="bulk-delete-btn"
      >
        <Trash2 className="w-3 h-3 mr-1" />
        Delete
      </Button>
    </div>
  );
};

export default BulkActionBar;
